import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LifeBuoy, Phone, ChevronDown } from "lucide-react";
import { Logo } from "../brand/Logo";
import { CONTACT } from "../../data/content";

const NAV = [
  { to: "/services", label: "Services", id: "services" },
  { to: "/solutions/ouvrir-mes-locaux", label: "Solutions", id: "solutions", match: "/solutions" },
  { to: "/missions", label: "Missions", id: "missions" },
  { label: "Réalisations", id: "realisations", match: ["/realisations", "/actualites"], children: [["/realisations/references", "Références", "references"], ["/realisations/actus", "Actus", "actus"]] },
  { label: "À propos", id: "apropos", match: ["/a-propos", "/contact"], children: [["/a-propos", "Qui sommes-nous", "qui-sommes-nous"], ["/contact", "Contact", "contact"]] },
];

const isActive = (item, pathname) => {
  const m = item.match || item.to;
  return (Array.isArray(m) ? m : [m]).some((p) => pathname.startsWith(p));
};

const linkCls = (active) => `nav-link text-sm font-medium ${active ? "text-white active" : "text-white/70 hover:text-white"}`;

const Dropdown = ({ item, active }) => (
  <div className="relative group" data-testid={`nav-dropdown-${item.id}`}>
    <button type="button" data-testid={`nav-link-${item.id}`} className={`${linkCls(active)} flex items-center gap-1`}>
      {item.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
    </button>
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-[opacity,transform,visibility] duration-200">
      <div className="min-w-[200px] bg-ink/95 backdrop-blur-xl border border-white/10 shadow-2xl py-2">
        {item.children.map(([to, label, id]) => (
          <NavLink key={to} to={to} data-testid={`nav-sub-${id}`}
            className={({ isActive: a }) => `block px-5 py-2.5 text-sm transition-colors ${a ? "text-white bg-white/5" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  </div>
);

const MobileMenu = () => (
  <nav data-testid="mobile-menu" className="lg:hidden border-t border-white/10 px-5 pb-6 pt-2 flex flex-col">
    {NAV.map((item) => item.children ? (
      <div key={item.id} className="py-3 border-b border-white/5">
        <span className="text-lg text-white/85 font-display">{item.label}</span>
        <div className="mt-1 flex flex-col pl-4">
          {item.children.map(([to, label, id]) => <NavLink key={to} to={to} data-testid={`mobile-nav-${id}`} className="py-2 text-white/65">{label}</NavLink>)}
        </div>
      </div>
    ) : (
      <NavLink key={item.id} to={item.to} data-testid={`mobile-nav-${item.id}`} className="py-3 text-lg text-white/85 border-b border-white/5 font-display">{item.label}</NavLink>
    ))}
    <NavLink to="/support" data-testid="mobile-nav-support" className="py-3 text-lg text-white/85 font-display">Assistance</NavLink>
  </nav>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, [pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled || open ? "bg-ink/95 backdrop-blur-xl shadow-lg" : "bg-ink/70 backdrop-blur-md"}`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="shrink-0" onClick={() => { window.scrollTo(0, 0); }}>
          <Logo theme="#45ADE0" size="sm" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => item.children
            ? <Dropdown key={item.id} item={item} active={isActive(item, pathname)} />
            : <Link key={item.id} to={item.to} data-testid={`nav-link-${item.id}`} className={linkCls(isActive(item, pathname))}>{item.label}</Link>)}
        </nav>
        <div className="flex items-center gap-2">
          <a href={CONTACT.hotlineHref} data-testid="header-call-btn" className="lg:hidden h-10 w-10 grid place-items-center rounded-full bg-white/10 text-white" aria-label="Appeler">
            <Phone size={18} />
          </a>
          <span className="hidden sm:block">
            <Link to="/support" data-testid="header-support-btn" className="btn-ghost-light">
              <LifeBuoy size={16} /> Assistance
            </Link>
          </span>
          <Link to="/devis" data-testid="header-quote-btn" className="btn-primary !h-10 !px-4 text-sm">
            <span className="hidden sm:inline">Demander un devis</span><span className="sm:hidden">Devis</span>
          </Link>
          <button data-testid="header-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden h-10 w-10 grid place-items-center text-white" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && <MobileMenu />}
    </header>
  );
};
